import { Component, Input, OnInit, inject } from '@angular/core';
import { fadeInOnEnterAnimation } from 'angular-animations';

import { TimeService } from '@shared';

import { TotalKeyNumbers } from '../models/total-key-numbers.model';
import { TimeView } from './models/time-view.model';
import { CountView } from './models/count-view.model';
import { ProblemInstanceService } from '../services/problem-instance/problem-instance.service';

@Component({
    selector: 'app-route-key-numbers',
    templateUrl: './route-key-numbers.component.html',
    styleUrls: ['./route-key-numbers.component.scss'],
    animations: [fadeInOnEnterAnimation({ duration: 500 })],
    standalone: false
})
export class RouteKeyNumbersComponent implements OnInit {
    private readonly timeService = inject(TimeService);
    private readonly problemInstance = inject(ProblemInstanceService);

    @Input() public showUnallocatedTasks: boolean;
    @Input() public showAllocatedTask: boolean;
    @Input() public showTaskTime: boolean;
    @Input() public showPatientTime: boolean;
    @Input() public printView: boolean;
    @Input() public whiteBackground: boolean;
    @Input() public routesLength: number;

    @Input() public set keyNumbers(value: TotalKeyNumbers) {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        if (value) {
            this.overqualificationCount = {
                count: value.overqualifiedWork,
                increase: value.overqualifiedWork > this._totalKeyNumbers?.overqualifiedWork,
                decrease: value.overqualifiedWork < this._totalKeyNumbers?.overqualifiedWork
            };

            this.overqualificationNumberOfTasksCount = {
                count: value.numberOfTasksWithOverqualifiedWork,
                increase: value.numberOfTasksWithOverqualifiedWork > this._totalKeyNumbers?.numberOfTasksWithOverqualifiedWork,
                decrease: value.numberOfTasksWithOverqualifiedWork < this._totalKeyNumbers?.numberOfTasksWithOverqualifiedWork
            };

            this.employeesCount = {
                count: value.totalNumberOfPatientsVisitedByEmployeesNotInPrimaryGroup,
                increase:
                    value.totalNumberOfPatientsVisitedByEmployeesNotInPrimaryGroup >
                    this._totalKeyNumbers?.totalNumberOfPatientsVisitedByEmployeesNotInPrimaryGroup,
                decrease:
                    value.totalNumberOfPatientsVisitedByEmployeesNotInPrimaryGroup <
                    this._totalKeyNumbers?.totalNumberOfPatientsVisitedByEmployeesNotInPrimaryGroup
            };

            this.overMaxEmployeesCount = {
                count: value.numberOfPatientsVisitedByOverMaxEmployees,
                increase:
                    value.numberOfPatientsVisitedByOverMaxEmployees >
                    this._totalKeyNumbers?.numberOfPatientsVisitedByOverMaxEmployees,
                decrease:
                    value.numberOfPatientsVisitedByOverMaxEmployees <
                    this._totalKeyNumbers?.numberOfPatientsVisitedByOverMaxEmployees
            };

            this.unallocatedTasks = {
                count: value.numberOfNonAllocatedTasksFromSearch,
                increase:
                    value.numberOfNonAllocatedTasksFromSearch >
                    this._totalKeyNumbers?.numberOfNonAllocatedTasksFromSearch,
                decrease:
                    value.numberOfNonAllocatedTasksFromSearch <
                    this._totalKeyNumbers?.numberOfNonAllocatedTasksFromSearch
            };

            this.unallocatedTasksPatients = {
                count: value.numberOfNonAllocatedTasks,
                increase:
                    value.numberOfNonAllocatedTasks >
                    this._totalKeyNumbers?.numberOfNonAllocatedTasks,
                decrease:
                    value.numberOfNonAllocatedTasksFromSearch <
                    this._totalKeyNumbers?.numberOfNonAllocatedTasks
            };

            this.tasksFromDifferentDepartmentCount = {
                count: value.numberOfTasksWithDifferentRosterDepartment,
                increase:
                    value.numberOfTasksWithDifferentRosterDepartment >
                    this._totalKeyNumbers?.numberOfTasksWithDifferentRosterDepartment,
                decrease:
                    value.numberOfTasksWithDifferentRosterDepartment <
                    this._totalKeyNumbers?.numberOfTasksWithDifferentRosterDepartment
            };

            this.lateTime = this.getTimeDifference(
                value.totalTimeBreakingTimeWindowsInSeconds,
                this._totalKeyNumbers?.totalTimeBreakingTimeWindowsInSeconds
            );

            this.travelTime = this.getTimeDifference(
                value.totalTravelTimeSeconds,
                this._totalKeyNumbers?.totalTravelTimeSeconds
            );

            this.patientTime = this.getTimeDifference(
                value.totalPatientTime,
                this._totalKeyNumbers?.totalPatientTime
            );

            this.taskTime = this.getTimeDifference(
                value.totalPatientTime + value.totalTimeForOfficeTasks,
                this._totalKeyNumbers?.totalPatientTime + this._totalKeyNumbers?.totalTimeForOfficeTasks
            );

            this.timeout = setTimeout(() => this.clearDiffState(), this.VisibleTime);
        } else {
            this.reset();
        }

        this._totalKeyNumbers = value;
    }

    public employeesCount: CountView;
    public overMaxEmployeesCount: CountView;
    public unallocatedTasks: CountView;
    public unallocatedTasksPatients: CountView;
    public overqualificationCount: CountView;
    public overqualificationNumberOfTasksCount: CountView;
    public tasksFromDifferentDepartmentCount: CountView;
    public lateTime: TimeView;
    public travelTime: TimeView;
    public patientTime: TimeView;
    public taskTime: TimeView;
    public travelTimeObjectiveEnabled: boolean;
    public timeWindowObjectiveEnabled: boolean;
    public visitHistoryObjectiveEnabled: boolean;
    public overqualificationObjectiveEnabled: boolean;
    public showTasksFromDifferentDepartment: boolean;
    public numberOfTasksWithoutMainResponsibles: number;
    public rosterInInstance: boolean;

    private readonly VisibleTime = 3000;
    private _totalKeyNumbers: TotalKeyNumbers;
    private timeout: any;

    ngOnInit(): void {
        this.problemInstance
            .getProblemInstance()
            .subscribe(
                result => {
                    (this.numberOfTasksWithoutMainResponsibles =
                        result.problemInstance.officeInfo.numberOfTasksWithoutMainResponsibles);
                    }
            );
        this.problemInstance.rosterDepartmentIsPartOfInstance()
            .pipe()
                .subscribe(result => this.rosterInInstance = result.valueOf());
        this.enableObjectives();
    }

    public getUnallocatedTooltipText(): string {
        if (this._totalKeyNumbers == undefined ||
            this._totalKeyNumbers.numberOfNonAllocatedTasksPerShift == undefined ||
            this._totalKeyNumbers.numberOfNonAllocatedTasksPerShift.length === 0) {
            return '';
        }
        const unallocatedShiftString = Object.entries(this._totalKeyNumbers.numberOfNonAllocatedTasksPerShift).reduce(
            (acc, [key, value], i) => {
                const shiftTypeEntry = key + ': ' + value;
               return  i === Object.keys(this._totalKeyNumbers.numberOfNonAllocatedTasksPerShift).length - 1 ? acc + shiftTypeEntry : acc + shiftTypeEntry + ', ';
            }, ''
        );
        return '. ' + unallocatedShiftString;
    }

    public reset(): void {
        this.lateTime = null;
        this.travelTime = null;
        this.patientTime = null;
        this.employeesCount = null;
        this.overMaxEmployeesCount = null;
        this.unallocatedTasks = null;
        this.unallocatedTasksPatients = null;
        this.overqualificationCount = null;
        this.overqualificationNumberOfTasksCount = null;
        this.tasksFromDifferentDepartmentCount = null;
        this._totalKeyNumbers = null;
        this.enableObjectives();
    }

    private getTimeDifference(totalTime: number, prevTotalTime: number): TimeView {
        const totalTimeInMinutes = Math.floor(totalTime / 60);
        const prevTotalTimeInMinutes = Math.floor(prevTotalTime / 60);
        const timeDifference = totalTimeInMinutes - prevTotalTimeInMinutes;

        return {
            hours: this.timeService.extractHours(totalTime),
            minutes: this.timeService.extractMinutes(totalTime),
            increase: timeDifference > 0,
            decrease: timeDifference < 0
        };
    }

    private clearDiffState(): void {
        if (this.overqualificationCount) {
            this.overqualificationCount.increase = null;
            this.overqualificationCount.decrease = null;
        }

        if (this.overqualificationNumberOfTasksCount) {
            this.overqualificationNumberOfTasksCount.increase = null;
            this.overqualificationNumberOfTasksCount.decrease = null;
        }

        if (this.employeesCount) {
            this.employeesCount.increase = null;
            this.employeesCount.decrease = null;
        }

        if (this.overMaxEmployeesCount) {
            this.overMaxEmployeesCount.increase = null;
            this.overMaxEmployeesCount.decrease = null;
        }

        if (this.unallocatedTasks) {
            this.unallocatedTasks.increase = null;
            this.unallocatedTasks.decrease = null;
        }

        if(this.unallocatedTasksPatients) {
            this.unallocatedTasksPatients.increase = null;
            this.unallocatedTasksPatients.decrease = null;
        }

        if (this.tasksFromDifferentDepartmentCount) {
            this.tasksFromDifferentDepartmentCount.increase = null;
            this.tasksFromDifferentDepartmentCount.decrease = null;
        }

        if (this.lateTime) {
            this.lateTime.increase = null;
            this.lateTime.decrease = null;
        }

        if (this.patientTime) {
            this.patientTime.increase = null;
            this.patientTime.decrease = null;
        }

        if (this.travelTime) {
            this.travelTime.increase = null;
            this.travelTime.decrease = null;
        }
    }

    private enableObjectives(): void {
        this.problemInstance.getProblemInstance().subscribe(result => {
            this.travelTimeObjectiveEnabled = result.problemInstance.officeInfo.travelTimeWeight >= 1;
            this.timeWindowObjectiveEnabled = result.problemInstance.officeInfo.timeWindowWeight >= 1;
            this.visitHistoryObjectiveEnabled = result.problemInstance.officeInfo.visitHistoryWeight >= 1;
            this.overqualificationObjectiveEnabled = result.problemInstance.officeInfo.overqualifiedWeight >= 1;
            this.showTasksFromDifferentDepartment = result.problemInstance.rosterDepartments != null;
        });
    }
}
